import { useState, useEffect, useCallback } from 'react';

const GUEST_TRIAL_DURATION_MS = 2 * 60 * 1000; // 2 minutes
const GUEST_TRIAL_KEY = 'guest_trial_start';

export function useGuestTrial() {
  const [isGuest, setIsGuest] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const [remainingTime, setRemainingTime] = useState(GUEST_TRIAL_DURATION_MS);

  const startGuestTrial = useCallback(() => {
    const existingStart = localStorage.getItem(GUEST_TRIAL_KEY);
    if (!existingStart) {
      localStorage.setItem(GUEST_TRIAL_KEY, Date.now().toString());
    }
    setIsGuest(true);
  }, []);

  const clearGuestTrial = useCallback(() => {
    localStorage.removeItem(GUEST_TRIAL_KEY);
    setIsGuest(false);
    setTrialExpired(false);
    setRemainingTime(GUEST_TRIAL_DURATION_MS);
  }, []);

  useEffect(() => {
    if (!isGuest) return;

    const checkExpiration = () => {
      const startTime = localStorage.getItem(GUEST_TRIAL_KEY);
      if (!startTime) {
        startGuestTrial();
        return;
      }

      const elapsed = Date.now() - parseInt(startTime, 10);
      const remaining = Math.max(0, GUEST_TRIAL_DURATION_MS - elapsed);
      
      setRemainingTime(remaining);
      
      if (remaining <= 0) {
        setTrialExpired(true);
      }
    };

    // Check immediately
    checkExpiration();

    // Check every second
    const interval = setInterval(checkExpiration, 1000);

    return () => clearInterval(interval);
  }, [isGuest, startGuestTrial]);

  const formatRemainingTime = useCallback(() => {
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [remainingTime]);

  return {
    isGuest,
    trialExpired,
    remainingTime,
    formatRemainingTime,
    startGuestTrial,
    clearGuestTrial,
  };
}
